import { sfx } from "../audio.ts";
import { ui } from "../engine.ts";

type Child = Node | string | number | null | undefined | false;
type Props = Record<string, unknown> & { class?: string; style?: Partial<CSSStyleDeclaration> };

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Props | null = null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(props ?? {})) {
    if (v === undefined || v === null || v === false) continue;
    if (k === "class") el.className = String(v);
    else if (k === "style") Object.assign(el.style, v);
    else if (k.startsWith("on") && typeof v === "function") el.addEventListener(k.slice(2), v as EventListener);
    else if (k in el) Reflect.set(el, k, v);
    else el.setAttribute(k, String(v));
  }
  append(el, ...children);
  return el;
}

export function append(el: Element, ...children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    el.append(c instanceof Node ? c : String(c));
  }
}

/** A game button that plays the click sound. */
export function btn(label: Child | Child[], onClick: () => void, cls = "", props: Props = {}): HTMLButtonElement {
  const kids = Array.isArray(label) ? label : [label];
  return h(
    "button",
    {
      class: `btn ${cls}`,
      ...props,
      onclick: () => {
        sfx.click();
        onClick();
      },
    },
    ...kids,
  );
}

let keyHandler: ((e: KeyboardEvent) => void) | null = null;
window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  keyHandler?.(e);
});

/** Replaces everything in the UI layer and installs the screen's key handler. */
export function mount(keys: ((e: KeyboardEvent) => void) | null, ...nodes: Child[]): void {
  keyHandler = keys;
  ui.replaceChildren();
  append(ui, ...nodes);
}

export function coin(): HTMLElement {
  return h("i", { class: "coin" });
}

export function bar(kind: string, frac: number, label: string): HTMLDivElement {
  return h("div", { class: `bar ${kind}` }, h("i", { style: { transform: `scaleX(${Math.max(0, Math.min(1, frac))})` } }), h("b", null, label));
}

export function setBar(el: HTMLElement, frac: number, label: string): void {
  const fill = el.querySelector<HTMLElement>("i:not(.ghost)");
  if (fill) fill.style.transform = `scaleX(${Math.max(0, Math.min(1, frac))})`;
  const ghost = el.querySelector<HTMLElement>("i.ghost");
  if (ghost) ghost.style.transform = `scaleX(${Math.max(0, Math.min(1, frac))})`;
  const text = el.querySelector("b");
  if (text) text.textContent = label;
}

export function toast(text: string, ms = 1600): void {
  const t = h("div", { class: "toast panel" }, text);
  ui.append(t);
  setTimeout(() => t.remove(), ms);
}

export function wait(ms: number): Promise<void> {
  const { promise, resolve } = Promise.withResolvers<void>();
  setTimeout(resolve, ms);
  return promise;
}
